import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * A cart-drawer upsell product card with inline variant selection
 * (rendered by `snippets/cart-upsell.liquid`).
 *
 * Resolves the selected option values against the embedded
 * `script[data-upsell-variants]` JSON map, keeps the nested product form's
 * hidden `input[name="id"]` in sync, swaps the color image and updates the
 * price. Cart mutations hydration-morph the whole drawer section, which
 * resets radios and images to their server-rendered defaults — the current
 * selection is kept in an instance field (the card node survives morphs via
 * its stable id) and re-applied after the cart update settles, mirroring the
 * timing pattern in `cart-drawer.js`.
 *
 * @extends {Component}
 */
class CartUpsellCardComponent extends Component {
  /** @type {string[] | null} */
  #selection = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#apply();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
  }

  /**
   * Handles option radio changes (delegated via `on:change` on the options wrapper).
   */
  handleOptionChange() {
    this.#selection = this.#readSelection();
    this.#apply();
  }

  /**
   * Reads the checked value per rendered option, keyed by option position
   * (`data-option-index`), NOT DOM order — fieldsets are rendered
   * sizes-before-color, and single-value options are not rendered at all
   * (sparse entries stay undefined and match any variant value).
   *
   * @returns {(string | null | undefined)[]}
   */
  #readSelection() {
    /** @type {(string | null | undefined)[]} */
    const selection = [];
    for (const fieldset of this.querySelectorAll('fieldset[data-option-index]')) {
      const index = Number(/** @type {HTMLElement} */ (fieldset).dataset.optionIndex);
      const checked = /** @type {HTMLInputElement | null} */ (fieldset.querySelector('input:checked'));
      selection[index] = checked ? checked.value : null;
    }
    return selection;
  }

  /** @returns {{ colorIndex: number | null, variants: { id: number, options: string[], available: boolean, price: string, compareAtPrice: string | null }[] } | null} */
  #variantData() {
    const script = this.querySelector('script[data-upsell-variants]');
    if (!script) return null;

    try {
      return JSON.parse(script.textContent || '');
    } catch {
      return null;
    }
  }

  /**
   * Syncs hidden variant id, price, button state and color image to the
   * current selection. Parses the JSON fresh each time because the morph may
   * have updated availability.
   */
  #apply() {
    const data = this.#variantData();
    if (!data) return;

    const selection = this.#selection ?? this.#readSelection();
    if (!selection.some((value) => value != null)) return;

    // Unrendered single-value options leave a hole in the selection — any
    // variant value matches there (all variants share that value anyway).
    const variant = data.variants.find((candidate) =>
      candidate.options.every((option, index) => selection[index] == null || option === selection[index])
    );

    if (variant) {
      const idInput = /** @type {HTMLInputElement | null} */ (this.querySelector('form input[name="id"]'));
      if (idInput) {
        idInput.value = String(variant.id);
        idInput.disabled = false;
      }

      const currentPrice = this.querySelector('.sp-upsell__price-current');
      if (currentPrice) {
        currentPrice.textContent = variant.price;
        currentPrice.classList.toggle('sp-upsell__price-current--sale', variant.compareAtPrice != null);
      }

      const comparePrice = this.querySelector('.sp-upsell__price-compare');
      if (comparePrice) {
        comparePrice.textContent = variant.compareAtPrice ?? '';
        comparePrice.toggleAttribute('hidden', variant.compareAtPrice == null);
      }
    }

    const addButton = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('add-to-cart-component [ref="addToCartButton"]')
    );
    if (addButton) addButton.disabled = !(variant && variant.available);

    if (data.colorIndex != null) {
      const selectedColor = selection[data.colorIndex];
      if (selectedColor != null) {
        for (const image of this.querySelectorAll('[data-color-value]')) {
          image.toggleAttribute('hidden', /** @type {HTMLElement} */ (image).dataset.colorValue !== selectedColor);
        }
      }
    }
  }

  /**
   * Re-checks the radios from the stored selection (the morph reset them to
   * server defaults) and re-applies the derived state.
   */
  #restoreSelection() {
    const selection = this.#selection;
    if (!selection) return;

    for (const fieldset of this.querySelectorAll('fieldset[data-option-index]')) {
      const value = selection[Number(/** @type {HTMLElement} */ (fieldset).dataset.optionIndex)];
      if (value == null) continue;
      for (const input of fieldset.querySelectorAll('input')) {
        /** @type {HTMLInputElement} */ (input).checked = /** @type {HTMLInputElement} */ (input).value === value;
      }
    }

    this.#apply();
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    // Captured at dispatch time: the morph removes the purchased card, which
    // otherwise makes the snap container jump (re-snap + scroll anchoring).
    const track = this.closest('.sp-upsell__track');
    const trackScrollLeft = track ? track.scrollLeft : null;

    event.promise
      ?.then(() =>
        requestAnimationFrame(() => {
          if (track?.isConnected && trackScrollLeft != null) {
            track.scrollLeft = trackScrollLeft;
          }
          if (this.isConnected) this.#restoreSelection();
        })
      )
      .catch(() => {});
  };
}

if (!customElements.get('cart-upsell-card-component')) {
  customElements.define('cart-upsell-card-component', CartUpsellCardComponent);
}

/**
 * Drag-to-scroll for the upsell track (mouse only — touch scrolls natively).
 * Delegated on `document` so it survives the drawer's hydration morphs.
 */
const DRAG_THRESHOLD = 5;

/** @type {HTMLElement | null} */
let dragTrack = null;
let dragStartX = 0;
let dragStartScrollLeft = 0;
let dragMoved = false;
let suppressNextClick = false;

document.addEventListener('pointerdown', (event) => {
  if (event.pointerType !== 'mouse' || event.button !== 0) return;
  const track = event.target instanceof Element ? /** @type {HTMLElement | null} */ (event.target.closest('.sp-upsell__track')) : null;
  if (!track) return;

  dragTrack = track;
  dragStartX = event.clientX;
  dragStartScrollLeft = track.scrollLeft;
  dragMoved = false;
});

document.addEventListener('pointermove', (event) => {
  if (!dragTrack) return;

  const delta = event.clientX - dragStartX;
  if (!dragMoved && Math.abs(delta) < DRAG_THRESHOLD) return;

  dragMoved = true;
  dragTrack.classList.add('sp-upsell__track--dragging');
  dragTrack.scrollLeft = dragStartScrollLeft - delta;
});

const endUpsellDrag = () => {
  if (!dragTrack) return;
  dragTrack.classList.remove('sp-upsell__track--dragging');
  suppressNextClick = dragMoved;
  dragTrack = null;
  dragMoved = false;
};

document.addEventListener('pointerup', endUpsellDrag);
document.addEventListener('pointercancel', endUpsellDrag);

// After a drag, swallow the click that would otherwise trigger the card link
// or toggle a swatch/pill under the cursor.
document.addEventListener(
  'click',
  (event) => {
    if (!suppressNextClick) return;
    suppressNextClick = false;
    if (event.target instanceof Element && event.target.closest('.sp-upsell__track')) {
      event.preventDefault();
      event.stopPropagation();
    }
  },
  true
);

// Native image drag would hijack the pointer gesture.
document.addEventListener('dragstart', (event) => {
  if (event.target instanceof Element && event.target.closest('.sp-upsell__track')) {
    event.preventDefault();
  }
});
